/**
 * Context object, defaults, types and `useAdminSettings` hook for the
 * admin-settings store. Split out of `admin-settings.store.tsx` so that
 * provider file only exports React components — keeps Vite's React Fast
 * Refresh happy.
 */
import { createContext, useContext } from 'react';
import type {
  AdminSettingsCategory,
  AdminSettingsPayload,
} from '@/src/modules/services/admin-settings.service';

export const LOCAL_KEY = 'admin-portal.settings.v1';

export const DEFAULT_SETTINGS: AdminSettingsPayload = {
  appearance: {
    themeMode: 'system',
    primaryColor: '#c0c1ff',
    secondaryColor: '#6f00be',
    accentColor: '#f751a1',
    borderRadius: 12,
    fontFamily: 'Inter',
    fontSize: 14,
    compactMode: false,
    cardStyle: 'glass',
    animationsEnabled: true,
  },
  layout: {
    sidebarCollapsed: false,
    sidebarPosition: 'left',
    stickyHeader: true,
    contentWidth: 'fluid',
    gridSpacing: 16,
    dashboardDensity: 'comfortable',
    navigationStyle: 'sidebar',
    topbarVisible: true,
  },
  dashboard: {
    landingPage: '/dashboard',
    widgetVisibility: { stats: true, activity: true, chart: true, shortcuts: true },
    widgetOrder: ['stats', 'chart', 'activity', 'shortcuts'],
    recentActivityPanel: true,
    quickActionShortcuts: ['create-invitation', 'browse-templates'],
    favoriteModules: [],
    refreshIntervalSeconds: 60,
  },
  notifications: {
    desktopNotifications: false,
    soundAlerts: false,
    emailNotifications: true,
    securityAlerts: true,
    taskReminders: true,
    approvalRequestAlerts: true,
    maintenanceNotifications: true,
  },
  dataView: {
    paginationSize: 20,
    tableDensity: 'comfortable',
    rememberFilters: true,
    rememberSorting: true,
    exportFormat: 'csv',
    autoSaveFilters: true,
    stickyTableHeaders: true,
  },
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    keyboardNavigation: true,
    screenReaderSupport: true,
    largerText: false,
    focusIndicators: true,
  },
  localization: {
    language: 'en',
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD MMM YYYY',
    timeFormat: '24h',
    numberFormat: 'en-IN',
  },
  productivity: {
    autoLogoutMinutes: 30,
    sessionTimeoutWarning: true,
    openLinksInNewTab: false,
    multiTabSync: true,
    autosaveDrafts: true,
    commandPaletteEnabled: true,
  },
};

export interface SettingsContextValue {
  settings: AdminSettingsPayload;
  loading: boolean;
  saving: boolean;
  error: string | null;
  updateCategory: <C extends AdminSettingsCategory>(
    category: C,
    values: Partial<AdminSettingsPayload[C]>,
  ) => Promise<void>;
  resetCategory: <C extends AdminSettingsCategory>(category: C) => Promise<void>;
  resetAll: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const SettingsContext = createContext<SettingsContextValue | null>(null);

export function useAdminSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx)
    throw new Error('useAdminSettings must be used inside AdminSettingsProvider');
  return ctx;
}
